import { ERROR_SEPARATOR } from '@/utils';
import { defu } from 'defu';
import { type UseFetchOptions } from 'nuxt/app';
import type { FetchResponse } from 'ofetch';

type FetchOptions<T> = UseFetchOptions<T> & {
  timeout?: number;
};
type ErrorType = { errors: Record<string, string[]> };

export const useAPI = <T = unknown>(
  url: string | (() => string),
  userOptions: FetchOptions<T> = {}
) => {
  const config = useRuntimeConfig();

  const controller = new AbortController();

  const timeoutId = setTimeout(() => {
    controller.abort(
      createError({
        statusCode: 408,
        statusMessage: 'aborted',
        message: 'This request has been automatically aborted.'
      })
    );
  }, userOptions.timeout);

  const defaultOptions: FetchOptions<T> = {
    //@ts-ignore
    baseURL: config.public.ApiBaseUrl,
    method: 'GET',
    retry: 3,
    timeout: 15000,
    signal: userOptions.timeout ? controller.signal : undefined,
    credentials: 'include',

    // cache request
    key:
      userOptions.cache === 'no-store'
        ? undefined
        : typeof url === 'string'
          ? url
          : url(),
    onRequest({ options }: { options: FetchOptions<T> }) {
      const isFormData = options.body instanceof FormData;

      options.headers = {
        Accept: 'application/json',
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(options.headers || {}) // allow overrides passed from outside
      };
    },
    onResponse({ response }: { response: FetchResponse<any> }) {
      const hasError =
        !response.status.toString().startsWith('2') ||
        response._data.status === 'error';

      if (hasError) {
        throw createError({
          statusCode: response.status,
          statusMessage: response._data.status,
          message:
            response._data?.message || JSON.stringify(response._data?.errors)
        });
      }
    },
    onResponseError({ response }: { response: FetchResponse<any> }) {
      const statusCode = response.status || 500;
      const statusMessage = response.statusText || '';
      const errorsMsg = (response._data || {}) as ErrorType;

      const errorEntries = Object.entries(errorsMsg.errors);

      const message = errorEntries.reduce((acc: string[], [key, value]) => {
        return [...acc, ...value.map((item) => `${key} ${item}`)];
      }, []);

      throw createError({
        statusCode,
        statusMessage,
        message: message.join(ERROR_SEPARATOR)
      });
    }
  };

  const options = defu(userOptions, defaultOptions) as UseFetchOptions<T>;

  const fetchResult = useFetch(url, options);

  return fetchResult
    .then((result) => {
      // If there's an error in the result, throw it to trigger catch blocks
      if (result.error.value) {
        throw result.error.value;
      }

      return result;
    })
    .finally(() => {
      if (userOptions.timeout && timeoutId) {
        clearTimeout(timeoutId);
      }
    });
};
